import React, { useEffect, useRef, useState } from 'react';
import { useAds } from '../../contexts/AdContext';
import { isWeb } from '../../utils/platform';
import adService from '../../services/adService';
import './NativeAd.css';

/**
 * Issue #311: Native Ad Component
 * - Blends with feed content for non-intrusive advertising
 * - Displayed every 5 posts in the feed
 * - Supports both web (demo/AdSense) and mobile (AdMob)
 */

// Google's official test ad slot for native ads
const TEST_AD_CLIENT = 'ca-pub-3940256099942544';

function NativeAd({ slot, index }) {
  const { isInitialized, demoMode, consentGiven } = useAds();
  const adRef = useRef(null);
  const adPushed = useRef(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isInitialized || demoMode) return;

    if (isWeb() && adRef.current && !adPushed.current) {
      try {
        adService.showNativeAd();
        adPushed.current = true;
      } catch (error) {
        // Silent failure - graceful degradation
      }
    }
  }, [isInitialized, demoMode]);

  if (!isInitialized || dismissed) {
    return null;
  }

  const isPersonalized = consentGiven === true;
  const adTypeLabel = isPersonalized ? 'Personalized' : 'NPA';
  const adColor = isPersonalized ? '#4CAF50' : '#FF9800';

  // Demo mode: show native ad that blends with feed
  if (demoMode) {
    return (
      <article
        className="native-ad-container"
        data-ad-slot={slot}
        data-ad-index={index}
        role="article"
        aria-label="Gesponsert"
      >
        <div className="native-ad-card">
          {/* Header - similar to post header */}
          <div className="native-ad-header">
            <div className="native-ad-author-section">
              <div
                className="native-ad-avatar"
                style={{ backgroundColor: adColor }}
                aria-hidden="true"
              >
                AD
              </div>
              <div className="native-ad-author-info">
                <span className="native-ad-sponsored">
                  Gesponsert
                  <span
                    className="native-ad-type-badge"
                    style={{ backgroundColor: adColor }}
                  >
                    {adTypeLabel}
                  </span>
                </span>
                <span className="native-ad-advertiser">Sample Advertiser</span>
              </div>
            </div>
            <button
              className="native-ad-close"
              onClick={() => setDismissed(true)}
              aria-label="Anzeige ausblenden"
            >
              &times;
            </button>
          </div>

          {/* Content - similar to post content */}
          <div className="native-ad-content">
            <h3 className="native-ad-headline">
              {isPersonalized
                ? 'Angebot speziell fur dich!'
                : 'Aktuelle Studenten-Angebote'}
            </h3>
            <p className="native-ad-description">
              {isPersonalized
                ? 'Entdecke personalisierte Deals und Rabatte basierend auf deinen Interessen.'
                : 'Allgemeine Anzeigen und Angebote fur Studierende in deiner Region.'}
            </p>
          </div>

          {/* Image placeholder */}
          <div className="native-ad-image" aria-hidden="true">
            <div
              className="native-ad-image-placeholder"
              style={{ backgroundColor: adColor }}
            >
              <span>Werbebild</span>
            </div>
          </div>

          {/* CTA */}
          <div className="native-ad-cta">
            <button
              className="native-ad-cta-button"
              style={{ backgroundColor: adColor }}
            >
              Mehr erfahren
            </button>
          </div>
        </div>
      </article>
    );
  }

  // Render Google AdSense native ad
  if (isWeb()) {
    return (
      <article
        className="native-ad-container"
        data-ad-slot={slot}
        data-ad-index={index}
        role="article"
        aria-label="Gesponsert"
      >
        <ins
          className="adsbygoogle"
          style={{ display: 'block' }}
          data-ad-client={TEST_AD_CLIENT}
          data-ad-slot={slot || 'native-feed'}
          data-ad-format="fluid"
          data-ad-layout-key="-6t+ed+2i-1n-4w"
          data-adtest="on"
          ref={adRef}
        />
      </article>
    );
  }

  return null;
}

export default NativeAd;
